import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { projectsApi, announcementsApi } from '@/lib/api';
import { AnnouncementBanner } from '@/components/ui/AnnouncementBanner';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { ProjectsCard } from '@/components/dashboard/ProjectsCard';
import { ActivityFeedCard } from '@/components/dashboard/ActivityFeedCard';
import { AgencyQueueCard } from '@/components/dashboard/AgencyQueueCard';
import { MyTasksCard } from '@/components/dashboard/MyTasksCard';
import { MyFormsCard } from '@/components/dashboard/MyFormsCard';
import { VersionDisplay } from '@/components/dashboard/VersionDisplay';

export function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Check if user is an agency member
  const isAgencyMember = user?.organizations?.some(
    (org: any) => org.type === 'AGENCY'
  );

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
  });

  const { data: announcements } = useQuery({
    queryKey: ['my-announcements'],
    queryFn: () => announcementsApi.getMyAnnouncements(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Auto-redirect if user has only one project
  useEffect(() => {
    if (!projectsLoading && projects?.length === 1) {
      navigate(`/projects/${projects[0].id}`, { replace: true });
    }
  }, [projects, projectsLoading, navigate]);

  // Show loading while checking project count
  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
      </div>
    );
  }

  // If single project, useEffect will redirect - show loading in meantime
  if (projects?.length === 1) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <DashboardHeader
        userName={user?.name || ''}
        unreadCount={unreadCount}
      />

      {/* Announcements */}
      {announcements && announcements.length > 0 && (
        <div className="mb-6">
          <AnnouncementBanner announcements={announcements} />
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left column - activity feed */}
        <div className="lg:col-span-8">
          <ActivityFeedCard
            userId={user?.id}
            onUnreadCountChange={setUnreadCount}
          />
        </div>

        {/* Right column - sidebar widgets */}
        <div className="lg:col-span-4 space-y-6">
          {/* Forms waiting on me (hidden when none) */}
          <MyFormsCard maxItems={5} />

          {/* My Tasks */}
          <MyTasksCard maxTasks={5} />

          {/* Agency Queue - only for agency members */}
          {isAgencyMember && <AgencyQueueCard maxItems={5} />}

          {/* Projects */}
          <ProjectsCard projects={projects || []} maxProjects={5} />
        </div>
      </div>

      {/* Version Display */}
      <VersionDisplay />
    </div>
  );
}
